import { useMemo, useState } from "react";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { api, apiError } from "../../lib/api";
import {
  useResource, PageTitle, Panel, Loading, ErrorState, EmptyState, Pill, DemoBadge,
  STATUS_TONE, fmtDateTime,
} from "../../components/app/Kit";

const VIEWS = ["Day", "Week", "Month"];
const SERVICES = ["New Patient Consultation", "Routine Cleaning", "Emergency Visit", "Cosmetic Consultation", "Follow-Up Review"];

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export default function Appointments() {
  const { data, state, error, reload } = useResource("/appointments");
  const customers = useResource("/customers");
  const [view, setView] = useState("Week");
  const [anchor, setAnchor] = useState(new Date());
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ customer_id: "", service: SERVICES[0], staff: "Dr. Alina Reyes", start: "", notes: "" });

  const range = useMemo(() => {
    const base = startOfDay(anchor);
    if (view === "Day") return [base, new Date(base.getTime() + 86400000)];
    if (view === "Week") {
      const start = new Date(base);
      start.setDate(base.getDate() - ((base.getDay() + 6) % 7));
      return [start, new Date(start.getTime() + 7 * 86400000)];
    }
    return [new Date(base.getFullYear(), base.getMonth(), 1), new Date(base.getFullYear(), base.getMonth() + 1, 1)];
  }, [anchor, view]);

  const visible = (data || []).filter((a) => {
    const t = new Date(a.start);
    return t >= range[0] && t < range[1];
  });

  const grouped = visible.reduce((acc, a) => {
    const key = new Date(a.start).toDateString();
    acc[key] = acc[key] || [];
    acc[key].push(a);
    return acc;
  }, {});

  const shift = (dir) => {
    const next = new Date(anchor);
    if (view === "Day") next.setDate(next.getDate() + dir);
    else if (view === "Week") next.setDate(next.getDate() + 7 * dir);
    else next.setMonth(next.getMonth() + dir);
    setAnchor(next);
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.start) {
      toast.error("Select a customer and a start time.");
      return;
    }
    const customer = (customers.data || []).find((c) => c.id === form.customer_id);
    try {
      await api.post("/appointments", {
        ...form,
        customer_name: customer?.name || "Unknown",
        start: new Date(form.start).toISOString(),
        end: new Date(new Date(form.start).getTime() + 45 * 60000).toISOString(),
        status: "scheduled",
      });
      toast.success("Appointment created in the CRM.");
      setCreating(false);
      setForm({ customer_id: "", service: SERVICES[0], staff: "Dr. Alina Reyes", start: "", notes: "" });
      reload();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const setStatus = async (a, status) => {
    try {
      await api.patch(`/appointments/${a.id}`, { status });
      toast.success(`Appointment ${status}.`);
      reload();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="appointments-page">
      <PageTitle
        testId="appointments-title"
        title="Appointments"
        sub="Day, week and month views with creation, rescheduling and cancellation."
        action={
          <div className="flex items-center gap-2">
            <DemoBadge />
            <button
              type="button"
              onClick={() => setCreating((v) => !v)}
              data-testid="appointments-new-button"
              className="inline-flex items-center gap-2 rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]"
            >
              <Icons.Plus className="h-3.5 w-3.5" /> New appointment
            </button>
          </div>
        }
      />

      <div
        data-testid="calendar-integration-notice"
        className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300/25 bg-amber-300/[0.05] p-4"
      >
        <p className="text-xs text-amber-200">
          <span className="font-medium">Google Calendar not connected.</span> Appointments are stored in Elvoria
          AI only. Connect a calendar provider to enable two-way sync.
        </p>
        <a
          href="/app/integrations"
          data-testid="appointments-connect-calendar"
          className="rounded-md border border-amber-300/30 px-3 py-1.5 text-[11px] text-amber-200 hover:bg-amber-300/10"
        >
          Connect Google Calendar
        </a>
      </div>

      {creating ? (
        <Panel testId="appointments-create-panel" title="New appointment" className="mb-4">
          <form onSubmit={create} className="grid gap-3 sm:grid-cols-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-elv-dim">Customer</label>
              <select
                value={form.customer_id}
                onChange={(e) => setForm((f) => ({ ...f, customer_id: e.target.value }))}
                data-testid="appointment-input-customer"
                className="mt-2 w-full rounded-md border border-elv-line bg-[#0A0F14] px-3 py-2 text-xs text-elv-text outline-none"
              >
                <option value="">Select…</option>
                {(customers.data || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-elv-dim">Service</label>
              <select
                value={form.service}
                onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
                data-testid="appointment-input-service"
                className="mt-2 w-full rounded-md border border-elv-line bg-[#0A0F14] px-3 py-2 text-xs text-elv-text outline-none"
              >
                {SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-elv-dim">Staff</label>
              <input
                value={form.staff}
                onChange={(e) => setForm((f) => ({ ...f, staff: e.target.value }))}
                data-testid="appointment-input-staff"
                className="mt-2 w-full rounded-md border border-elv-line bg-white/[0.02] px-3 py-2 text-xs text-elv-text outline-none focus:border-elv-green/40"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-elv-dim">Start</label>
              <input
                type="datetime-local"
                value={form.start}
                onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                data-testid="appointment-input-start"
                className="mt-2 w-full rounded-md border border-elv-line bg-white/[0.02] px-3 py-2 text-xs text-elv-text outline-none focus:border-elv-green/40"
              />
            </div>
            <div className="sm:col-span-4">
              <button type="submit" data-testid="appointment-create-submit" className="rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]">
                Create appointment
              </button>
            </div>
          </form>
        </Panel>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1.5">
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              data-testid={`calendar-view-${v.toLowerCase()}`}
              className={`rounded-md border px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] ${
                view === v ? "border-elv-green/35 bg-elv-green/[0.08] text-elv-green" : "border-elv-line bg-white/[0.02] text-elv-dim"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => shift(-1)} data-testid="calendar-prev" className="rounded-md border border-elv-line p-1.5 text-elv-dim hover:text-elv-text" aria-label="Previous">
            <Icons.ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span data-testid="calendar-range" className="text-xs text-elv-text">
            {range[0].toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} —{" "}
            {new Date(range[1].getTime() - 1).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
          </span>
          <button type="button" onClick={() => shift(1)} data-testid="calendar-next" className="rounded-md border border-elv-line p-1.5 text-elv-dim hover:text-elv-text" aria-label="Next">
            <Icons.ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {state === "loading" ? <Loading rows={6} testId="appointments-loading" /> : null}
      {state === "error" ? <ErrorState message={error} onRetry={reload} testId="appointments-error" /> : null}

      {state === "ready" && visible.length === 0 ? (
        <EmptyState
          icon="CalendarCheck"
          title="No appointments"
          sub="Connect your calendar to start booking automatically, or create one manually."
          action={
            <button type="button" onClick={() => setCreating(true)} data-testid="appointments-empty-cta" className="rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]">
              Create appointment
            </button>
          }
          testId="appointments-empty"
        />
      ) : null}

      {state === "ready" && visible.length > 0 ? (
        <div className="grid gap-3">
          {Object.entries(grouped).map(([day, items]) => (
            <Panel key={day} testId={`calendar-day-${day}`} title={day}>
              <ul className="grid gap-2">
                {items
                  .sort((a, b) => new Date(a.start) - new Date(b.start))
                  .map((a) => (
                    <li key={a.id} data-testid={`appointment-${a.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                      <div>
                        <p className="text-xs font-medium text-elv-text">
                          {fmtDateTime(a.start)} · {a.customer_name}
                        </p>
                        <p className="mt-1 text-[11px] text-elv-dim">
                          {a.service} · {a.staff} · {a.notes}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Pill tone={STATUS_TONE[a.status] || "dim"}>{a.status}</Pill>
                        <select
                          value={a.status}
                          onChange={(e) => setStatus(a, e.target.value)}
                          data-testid={`appointment-status-${a.id}`}
                          className="rounded-md border border-elv-line bg-[#0A0F14] px-2 py-1 text-[11px] text-elv-text outline-none"
                        >
                          {["scheduled", "completed", "cancelled", "no_show"].map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                    </li>
                  ))}
              </ul>
            </Panel>
          ))}
        </div>
      ) : null}
    </div>
  );
}
